import React, { useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faCircleCheck,
  faFileLines,
  faPlus,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { toast } from "react-toastify";
import axiosInstance from "../utils/axiosInstance";
import { ParseErrors } from "../utils/ParseErrors";
import { fileSize } from "../utils/chase-actions.constants";
import {
  NAMED_DOCS,
  money,
  numOrNull,
  toBase64,
  type ChaseDocument,
  type DocKind,
} from "../utils/create-chase.constants";
import type { ILoanDocument } from "../types/chase-type";

const clamp = (n: number) => Math.max(0, Math.min(100, n));
/** Base64 is a third bigger than the file it carries — report the file's size. */
const real = (base64Length: number) => Math.round((base64Length * 3) / 4);

/** The renewal pack on the chase, plus upload slots only when `onChange` is passed (create). */
const RenewalDocuments: React.FC<{
  documents?: ChaseDocument[];
  /** Create passes this; Edit doesn't, so it only lists the stored pack. */
  onChange?: (documents: ChaseDocument[]) => void;
  /** Already stored against the chase — read-only, opened through a signed link. */
  stored?: ILoanDocument[];
  /** The chase request is in flight — the rows lock and the bars run. */
  loading?: boolean;
  /** Bytes of the request body acknowledged so far — drives every bar. */
  loaded?: number;
}> = ({
  documents = [],
  onChange,
  stored = [],
  loading = false,
  loaded = 0,
}) => {
  const [otherKeys, setOtherKeys] = useState<string[]>([]);
  // An "other" row is whatever the agent calls it, so its label is typed.
  const [otherNames, setOtherNames] = useState<Record<string, string>>({});
  const [openingDoc, setOpeningDoc] = useState<number | null>(null);

  const rows = [
    ...NAMED_DOCS.map((d) => ({ ...d, removable: false })),
    ...otherKeys.map((key, i) => ({
      key,
      kind: "OTHER" as DocKind,
      name: otherNames[key]?.trim() || `Other Document ${i + 1}`,
      showFee: false,
      removable: true,
    })),
  ];
  const docAt = (key: string) => documents.find((d) => d.key === key);
  const setDoc = (row: (typeof rows)[number], patch: Partial<ChaseDocument>) => {
    // `file` carries the base64 the server puts on S3; a row without one is
    // just an empty slot.
    const next: ChaseDocument = {
      key: row.key,
      name: row.name,
      kind: row.kind,
      fileName: "",
      file: null,
      fee: null,
      ...docAt(row.key),
      ...patch,
    };
    const rest = documents.filter((d) => d.key !== row.key);
    onChange(next.fileName ? [...rest, next] : rest);
  };
  const removeDoc = (key: string) => {
    setOtherKeys((keys) => keys.filter((k) => k !== key));
    onChange(documents.filter((d) => d.key !== key));
  };
  // The typed label is the row's name, and the picked document's too.
  const nameDoc = (row: (typeof rows)[number], name: string) => {
    setOtherNames((names) => ({ ...names, [row.key]: name }));
    if (docAt(row.key)) setDoc(row, { name: name.trim() || row.name });
  };

  // ─── Progress ───────────────────────────────────────────────────────────────
  // One request carries the lot, so the browser only reports total bytes sent.
  // What goes on the wire is the base64, in `documents` order; each row's share
  // is that total minus everything queued ahead of it.
  const wire = (doc?: ChaseDocument) => doc?.file?.length ?? 0;
  const total = documents.reduce((sum, d) => sum + wire(d), 0);
  const overall = total ? clamp((loaded / total) * 100) : 0;
  const rowProgress = (key: string) => {
    const index = documents.findIndex((d) => d.key === key);
    if (index < 0) return 0;
    const before = documents
      .slice(0, index)
      .reduce((sum, d) => sum + wire(d), 0);
    return clamp(((loaded - before) / wire(documents[index])) * 100);
  };

  // Newest first — a re-renewal adds to the pack rather than replacing it.
  const attached = [...stored].sort((a, b) =>
    String(b.createdAt).localeCompare(String(a.createdAt)),
  );

  // The bucket is private and no link is stored, so one is minted per click.
  const OpenDocument = async (id: number) => {
    setOpeningDoc(id);
    try {
      const res = await axiosInstance.get(`/chase/document/${id}`);
      window.open(res.data.link, "_blank");
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setOpeningDoc(null);
    }
  };

  const attachedList = attached.length ? (
    <div className="mb-5 space-y-2">
      <p className="text-label-muted font-medium text-sm">
        Already attached ({attached.length})
      </p>
      {attached.map((doc) => (
        <button
          key={doc.id}
          type="button"
          onClick={() => OpenDocument(doc.id)}
          disabled={openingDoc === doc.id}
          className="w-full flex items-center gap-3 text-left rounded-xl border border-edge bg-surface-muted/60 p-3 hover:border-indigo-400/40 hover:bg-active-background transition-colors duration-200 disabled:opacity-60 disabled:cursor-wait"
        >
          <span className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center border border-edge bg-surface-elevated text-light-gray">
            <FontAwesomeIcon
              icon={openingDoc === doc.id ? faSpinner : faFileLines}
              spin={openingDoc === doc.id}
              className="text-sm"
            />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-medium text-heading-color truncate">
              {doc.name}
            </p>
            <p className="mt-0.5 text-[11px] text-light-gray truncate">
              {doc.fileName} · {fileSize(doc.fileSize)}
              {doc.createdAt
                ? ` · ${moment(doc.createdAt).format("DD MMM YYYY")}`
                : ""}
            </p>
          </div>
          {doc.fee ? (
            <span className="shrink-0 text-[13px] font-semibold text-heading-color tabular-nums">
              {money(Number(doc.fee))}
            </span>
          ) : null}
          <FontAwesomeIcon
            icon={faArrowUpRightFromSquare}
            className="shrink-0 text-xs text-light-gray"
          />
        </button>
      ))}
    </div>
  ) : null;

  if (!onChange)
    return (
      attachedList ?? (
        <p className="text-light-gray text-sm">No documents attached yet.</p>
      )
    );

  return (
    <>
      {attachedList}

      <div className="space-y-3">
        {rows.map((row) => {
          const doc = docAt(row.key);
          const percent = loading && doc ? rowProgress(row.key) : 0;
          const done = percent >= 100;
          return (
            <div
              key={row.key}
              className="rounded-xl border border-edge bg-surface-muted/60 p-3"
            >
              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                <div className="flex-1">
                  {row.removable ? (
                    <input
                      className="border border-input-border rounded-lg bg-input-bg text-text-input text-sm px-3 h-[34px] w-full sm:w-64 mb-2 outline-none transition-colors duration-200 focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500/30 disabled:opacity-60"
                      placeholder={row.name}
                      disabled={loading}
                      value={otherNames[row.key] ?? ""}
                      onChange={(e) => nameDoc(row, e.target.value)}
                    />
                  ) : (
                    <label className="text-label-muted font-medium block text-sm mb-1">
                      {row.name}
                    </label>
                  )}
                  <input
                    type="file"
                    disabled={loading}
                    className="block w-full text-sm text-text-primary file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border file:border-edge file:bg-surface-elevated file:text-text-primary file:text-sm file:cursor-pointer hover:file:border-indigo-400/40 disabled:opacity-60"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      setDoc(row, {
                        fileName: file?.name ?? "",
                        file: file ? await toBase64(file) : null,
                      });
                    }}
                  />
                </div>
                {/* A fee belongs to a document, so it opens once one is picked. */}
                {row.showFee && doc && (
                  <div className="w-full sm:w-40">
                    <label className="text-label-muted font-medium block text-sm mb-1">
                      Fee
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-light-gray text-sm">
                        £
                      </span>
                      <input
                        className="border border-input-border rounded-lg bg-input-bg text-text-input text-sm pl-6 pr-3 h-[40px] w-full outline-none transition-colors duration-200 focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500/30 disabled:opacity-60"
                        type="number"
                        disabled={loading}
                        value={doc.fee ?? ""}
                        onChange={(e) => setDoc(row, { fee: numOrNull(e.target.value) })}
                      />
                    </div>
                  </div>
                )}
                {row.removable && (
                  <button
                    disabled={loading}
                    className="text-red-400 text-sm font-medium px-3 h-[40px] rounded-lg hover:bg-red-500/10 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={() => removeDoc(row.key)}
                  >
                    Remove
                  </button>
                )}
              </div>

              {/* This file's share of the bytes sent so far. */}
              {loading && doc && (
                <div className="mt-2.5 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={done ? faCircleCheck : faSpinner}
                    spin={!done}
                    className={clsx(
                      "text-xs shrink-0",
                      done ? "text-emerald-500" : "text-light-gray",
                    )}
                  />
                  <div className="h-1 flex-1 rounded-full bg-overlay overflow-hidden">
                    <div
                      className={clsx(
                        "h-full rounded-full transition-[width] duration-200 ease-out",
                        done
                          ? "bg-emerald-500"
                          : "bg-gradient-to-r from-accent to-accent-bright",
                      )}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <span className="text-[11px] text-light-gray tabular-nums shrink-0">
                    {fileSize(real(wire(doc)))}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Overall bar — the whole pack rides in the one chase request. */}
      {loading && total > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-[12px] font-semibold">
            <span className="text-light-gray">
              {overall >= 100
                ? "Finishing up…"
                : `Uploading ${documents.length} document${documents.length > 1 ? "s" : ""}`}
            </span>
            <span className="text-heading-color tabular-nums">
              {Math.round(overall)}%
            </span>
          </div>
          <div className="mt-1.5 h-2 w-full rounded-full bg-overlay overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-accent to-accent-bright transition-[width] duration-200 ease-out"
              style={{ width: `${overall}%` }}
            />
          </div>
          <p className="mt-1 text-[11px] text-light-gray tabular-nums">
            {fileSize(real(Math.min(loaded, total)))} of {fileSize(real(total))}
          </p>
        </div>
      )}

      <button
        type="button"
        disabled={loading}
        className="mt-3 flex items-center gap-2 text-blue text-sm font-medium border border-edge rounded-lg px-3 py-1.5 hover:border-indigo-400/40 hover:bg-active-background transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
        onClick={() => setOtherKeys((keys) => [...keys, `OTHER_${Date.now()}`])}
      >
        <FontAwesomeIcon icon={faPlus} className="text-xs" />
        Add other
      </button>
    </>
  );
};

export default RenewalDocuments;
